import { useState } from 'react';
import axios from 'axios';
import { X, Loader2, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-screen modal that generates and displays a scripture-rooted prayer over
 * the current month's real budget numbers. Faith-forward, no manipulation.
 */
export default function BudgetPrayerModal({ month, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [prayer, setPrayer] = useState('');

  const load = async () => {
    setLoading(true);
    setPrayer('');
    try {
      const { data } = await axios.post(`${API_URL}/api/budget/${month}/pray`);
      setPrayer(data.prayer || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate prayer');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prayer);
      toast.success('Prayer copied to clipboard.');
    } catch {
      toast.error('Could not copy — long-press and select instead.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#36454F]/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="prayer-modal"
    >
      <div
        className="w-full max-w-2xl bg-white border border-[#36454F]/15 rounded-sm shadow-2xl overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#B76E79]/15 to-[#E0BFB8]/10 p-5 flex items-center justify-between border-b border-[#36454F]/10">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
              Pray over this budget
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mt-0.5">
              {prayer ? 'A prayer for your household' : 'Ready when you are'}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[#36454F]/40 hover:text-[#36454F] transition-colors"
            data-testid="prayer-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6">
          {!prayer && !loading && (
            <>
              <p className="text-sm text-[#36454F]/75 font-['Manrope'] leading-relaxed mb-5">
                Selah Slate will craft a warm, scripture-anchored prayer using your household&apos;s actual
                numbers from this month — income, envelopes, spending, and giving. It stays entirely
                on this screen; nothing is shared, and no one at Selah Slate ever sees your figures.
              </p>
              <Button
                onClick={load}
                className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                data-testid="prayer-generate-btn"
              >
                Compose a prayer
              </Button>
            </>
          )}
          {loading && (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#B76E79] mx-auto mb-3" strokeWidth={2} />
              <p className="text-sm text-[#36454F]/60 font-['Manrope']">Praying over your numbers…</p>
            </div>
          )}
          {prayer && !loading && (
            <>
              <div
                className="border-l-4 border-[#B76E79] pl-5 py-1 text-[15px] leading-relaxed font-['Playfair_Display'] italic text-[#36454F] whitespace-pre-wrap"
                data-testid="prayer-text"
              >
                {prayer}
              </div>
              <div className="flex items-center gap-2 mt-5">
                <Button
                  onClick={copy}
                  variant="outline"
                  className="h-9 border-[#B76E79]/30 text-[#B76E79] hover:bg-[#B76E79]/8 font-['Manrope'] rounded-sm text-sm"
                  data-testid="prayer-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Copy
                </Button>
                <Button
                  onClick={load}
                  variant="ghost"
                  className="h-9 text-[#36454F]/60 hover:text-[#36454F] font-['Manrope'] rounded-sm text-sm"
                  data-testid="prayer-regen-btn"
                >
                  Compose another
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
